import { getAdobeAccessToken } from '../auth/adobeIms';
import { actionUrl } from './database';
import type { TrackerAvailabilityState } from './tracker';
import { parseDemoExpertise, buildDemoExpertiseMessage, type DemoExpertise } from './demoExpertise';
import { initiateConversation, sendMessage, issuePageContext } from './aiAgent';
import type { RevealMap } from './pseudonymize';

// ============================================================================
// Types
// ============================================================================

export type PendingRequestSyncState = 'pending-sync' | 'synced' | 'failed';

export interface PendingRequestTeam {
  id: string;
  name: string;
}

export interface PendingRequestManualAssignment {
  scName: string;
  scEmail?: string;
  assignedBy: string;
  assignedByName: string | null;
  assignedAt: string;
  syncState: PendingRequestSyncState;
}

export interface PendingRequestWorkfrontAssignee {
  name: string;
  email: string | null;
}

export type PendingRequestEventType = 'reassignment' | 'manual-assignment' | 'ai-choice';

export interface PendingRequestEvent {
  type: PendingRequestEventType;
  from: string | null;
  to: string | null;
  at: string;
  actor?: string;
  actorName?: string | null;
  chosen?: { scName: string; scEmail?: string | null } | null;
  reason?: string | null;
  demoExpertise?: DemoExpertise;
}

/** The demo-expertise profile of the most recent ai-choice event, if any. */
export function latestAiChoiceDemoExpertise(events: PendingRequestEvent[]): DemoExpertise | undefined {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const ev = events[i];
    if (ev.type === 'ai-choice' && ev.demoExpertise) return ev.demoExpertise;
  }
  return undefined;
}

export interface PendingRequest {
  id: string;                         // Workfront OPTASK ID — stable key
  name: string;
  teams: PendingRequestTeam[];        // teams the issue is assigned to (id + name)
  status: string;                     // last known status from Workfront
  entryDate: string;
  products: string[];                 // products from the request form (parameterValues DE:TCS/TPS Adobe Products)
  workfrontAssignee: string | null;   // primary Workfront assignee (first name)
  workfrontAssignees: PendingRequestWorkfrontAssignee[]; // all Workfront-assigned users (name + email)
  manualAssignment: PendingRequestManualAssignment | null;
  inactive: boolean;
  lastSeenStatus: string;
  rawPayload: Record<string, unknown>;
}

export interface PendingRequestListParams {
  offset?: number;
  limit?: number;
  teamId?: string;
  active?: 'active' | 'inactive' | 'all';
  assignment?: 'assigned' | 'unassigned' | 'all';
  statusIn?: string[];
  search?: string;
  entryDateFrom?: string;
  entryDateTo?: string;
}

export interface PendingRequestListResponse {
  requests: PendingRequest[];
  count: number;
  totalCount: number;
  offset: number;
  limit: number;
  unassignedCount?: number;
  lastFetchedAt: string | null;
}

export interface PendingRequestSyncResult {
  synced: boolean;
  lastFetchedAt: string;
}

function firstString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  return s || fallback;
}

/** Products from the request form (parameterValues). Reads DE:TCS Adobe Products,
 *  falling back to the older DE:TPS Adobe Products key. Tolerates arrays,
 *  comma/semicolon-separated strings, and absent fields. */
export function extractProducts(raw: Record<string, unknown>): string[] {
  const pv = raw?.parameterValues;
  const value =
    pv && typeof pv === 'object' && !Array.isArray(pv)
      ? ((pv as Record<string, unknown>)['DE:TCS Adobe Products'] ??
        (pv as Record<string, unknown>)['DE:TPS Adobe Products'])
      : undefined;
  if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(/[,;]/)
      .map(v => v.trim())
      .filter(Boolean);
  }
  return [];
}

// ============================================================================
// Normalization — maps a raw OPTASK field=* payload to indexed display fields
// ============================================================================

export function normalizeRawPendingRequest(raw: Record<string, unknown>): PendingRequest {
  const id = firstString(raw.ID ?? raw.id);
  const teamsRaw = raw.assignedTeams ?? raw.team ?? [];
  const teams: PendingRequestTeam[] = Array.isArray(teamsRaw)
    ? teamsRaw
        .map(t => {
          if (typeof t === 'string') return { id: t, name: t };
          return { id: firstString(t?.ID ?? t?.id), name: firstString(t?.name ?? t?.teamName) };
        })
        .filter(t => t.id || t.name)
    : firstString(teamsRaw)
      ? [{ id: firstString(teamsRaw), name: firstString(teamsRaw) }]
      : [];

  const assignedToRaw = raw.assignedTo;
  const assignedToName =
    assignedToRaw && typeof assignedToRaw === 'object' && 'name' in assignedToRaw
      ? String((assignedToRaw as { name: unknown }).name ?? '')
      : '';

  const workfrontAssignees: PendingRequestWorkfrontAssignee[] = (() => {
    const seen = new Map<string, PendingRequestWorkfrontAssignee>();
    const push = (name: unknown, email: unknown) => {
      const n = firstString(name).trim();
      const e = firstString(email).trim();
      if (!n && !e) return;
      const key = e || n;
      if (!seen.has(key)) seen.set(key, { name: n, email: e || null });
    };
    const primary = raw.assignedTo;
    if (primary && typeof primary === 'object' && !Array.isArray(primary)) {
      const p = primary as { name?: unknown; emailAddr?: unknown; email?: unknown };
      push(p.name, p.emailAddr ?? p.email);
    }
    const assignments = Array.isArray(raw.assignments) ? raw.assignments : [];
    for (const a of assignments) {
      const at = a && typeof a === 'object' ? (a as { assignedTo?: unknown }).assignedTo : undefined;
      if (at && typeof at === 'object' && !Array.isArray(at)) {
        const u = at as { name?: unknown; emailAddr?: unknown; email?: unknown };
        push(u.name, u.emailAddr ?? u.email);
      } else {
        const al = a as { assignedToName?: unknown; assignedToEmail?: unknown };
        push(al.assignedToName, al.assignedToEmail);
      }
    }
    return Array.from(seen.values());
  })();

  return {
    id,
    name: firstString(raw.name ?? raw.issueName),
    teams,
    status: firstString(raw.status),
    entryDate: firstString(raw.entryDate ?? raw.entry),
    workfrontAssignee: assignedToName || firstString(raw.assignedToName) || null,
    workfrontAssignees,
    products: extractProducts(raw),
    manualAssignment: null,
    inactive: false,
    lastSeenStatus: firstString(raw.status),
    rawPayload: raw,
  };
}

// ============================================================================
// API actions
// ============================================================================

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function listPendingRequests(
  params: PendingRequestListParams = {}
): Promise<PendingRequestListResponse> {
  const token = getAdobeAccessToken();
  const resp = await fetch(actionUrl('db-list-pending-requests'), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify({
      offset: params.offset ?? 0,
      limit: params.limit ?? 50,
      teamId: params.teamId ?? undefined,
      active: params.active ?? 'all',
      assignment: params.assignment ?? 'all',
      statusIn: params.statusIn,
      search: params.search ?? undefined,
      entryDateFrom: params.entryDateFrom ?? undefined,
      entryDateTo: params.entryDateTo ?? undefined,
    }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'unknown' }));
    throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as PendingRequestListResponse;
}

export interface PendingRequestAssignee {
  name: string;
  email: string;
}

export async function assignPendingRequest(
  requestId: string,
  assignee: PendingRequestAssignee,
  actor: { email: string; displayName: string | null }
): Promise<PendingRequest> {
  const token = getAdobeAccessToken();
  const resp = await fetch(actionUrl('db-assign-pending-request'), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify({
      requestId,
      scName: assignee.name,
      scEmail: assignee.email,
      actor,
    }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'unknown' }));
    throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as PendingRequest;
}

/** Assignment-time refresh: re-fetch the Workfront state of a single pending
 *  request by ID right after it is assigned, so the row reflects its true state
 *  (assignee, status, historical/inactive) without a full sync sweep. */
export async function refreshPendingRequest(requestId: string): Promise<PendingRequest> {
  const token = getAdobeAccessToken();
  const resp = await fetch(actionUrl('db-refresh-pending-request'), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify({ requestId }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'unknown' }));
    throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as PendingRequest;
}

export async function deletePendingRequests(requestIds: string[]): Promise<{ deleted: number }> {
  const token = getAdobeAccessToken();
  const resp = await fetch(actionUrl('db-delete-pending-requests'), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify({ requestIds }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'unknown' }));
    throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as { deleted: number };
}

const SYNC_POLL_INTERVAL_MS = 3000;
const SYNC_TIMEOUT_MS = 5 * 60 * 1000;

// ============================================================================
// AI assign — advisory routing picks from ai-agent-ondemand-mcp (assign mode)
// ============================================================================

export interface AiAssignSuggestion {
  scName: string;
  scEmail?: string;
  full: boolean;
  maxLevel: number;
  inProgressCount: number;
}

export interface AiAssignRequestPayload {
  requestId: string;
  name: string;
  products: string[];
  suggestions: AiAssignSuggestion[];
  demoExpertise?: DemoExpertise;
}

export interface AiAssignChoice {
  requestId: string;
  scName: string;
  scEmail: string;
  reason: string;
}

/** Progress stage of one AI-assign call, surfaced in the AI choice column. */
export type AiAssignStage = 'loading-agent' | 'fetching-details' | 'making-suggestion';

/**
 * Progress callback for one AI-assign call. Carries the parsed demo-expertise
 * once the ai-agent-wf details fetch succeeds (stage transitions to
 * 'making-suggestion'), so the UI can show it immediately.
 */
export type AiAssignProgress = (
  requestId: string,
  stage: AiAssignStage,
  demoExpertise?: DemoExpertise
) => void;

const AI_ASSIGN_TIMEOUT_MS = 60_000;
const AI_EXPERTISE_CALL_TIMEOUT_MS = 30_000;
const AI_EXPERTISE_MAX_ATTEMPTS = 2;

/**
 * Ask ai-agent-wf to summarize a request's demo requirements (products,
 * timeline, audience, delivery mode, business ask) and the minimum expertise
 * levels (L1–L4) needed to deliver it. Returns the validated expertise or
 * throws on any failure. One isolated conversation per call.
 */
async function fetchDemoExpertise(
  requestId: string,
  signal?: AbortSignal,
  onProgress?: AiAssignProgress
): Promise<DemoExpertise> {
  let messageText = '';
  const collect = (evt: { event: string; data: string }) => {
    messageText += evt.data;
  };

  onProgress?.(requestId, 'loading-agent');
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_EXPERTISE_CALL_TIMEOUT_MS);
  if (signal) {
    signal.addEventListener('abort', () => controller.abort());
  }
  try {
    const conversationId = await initiateConversation(collect, controller.signal);
    if (!conversationId) throw new Error('ai-agent-wf did not return a conversation id');
    messageText = '';
    onProgress?.(requestId, 'fetching-details');
    await sendMessage(
      conversationId,
      buildDemoExpertiseMessage(requestId),
      collect,
      controller.signal,
      { includeContext: false, pageContext: issuePageContext(requestId) }
    );
    const expertise = parseDemoExpertise(messageText);
    if (!expertise) throw new Error('ai-agent-wf returned no valid demo-expertise JSON');
    onProgress?.(requestId, 'making-suggestion', expertise);
    return expertise;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch demo expertise with one automatic retry of the whole chain (a fresh
 * conversation each attempt). The caller is responsible for surfacing the
 * final failure.
 */
async function fetchDemoExpertiseWithRetry(
  requestId: string,
  signal?: AbortSignal,
  onProgress?: AiAssignProgress
): Promise<DemoExpertise> {
  let lastError: unknown;
  for (let attempt = 0; attempt < AI_EXPERTISE_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await fetchDemoExpertise(requestId, signal, onProgress);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}

/**
 * Ask the AI agent (assign mode) to pick one suggested SC per pending request.
 * For each request, first asks ai-agent-wf for the demo-expertise context
 * (retry once on failure), attaches it as `demoExpertise`, then sends the
 * enriched payload to ai-agent-ondemand-mcp. The backend authors the
 * selection rules and returns strict-JSON choices, each validated against the
 * submitted suggestion list. The picks are advisory — routing them still goes
 * through the manual-assign flow.
 */
export async function aiAssignPendingRequests(
  pendingRequests: AiAssignRequestPayload[],
  availability: TrackerAvailabilityState,
  signal?: AbortSignal,
  onProgress?: AiAssignProgress,
  revealMap?: RevealMap
): Promise<AiAssignChoice[]> {
  const enriched = [];
  for (const req of pendingRequests) {
    const demoExpertise = await fetchDemoExpertiseWithRetry(req.requestId, signal, onProgress);
    enriched.push({ ...req, demoExpertise });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_ASSIGN_TIMEOUT_MS);
  if (signal) {
    signal.addEventListener('abort', () => controller.abort());
  }
  try {
    const token = getAdobeAccessToken();
    const resp = await fetch(actionUrl('ai-agent-ondemand-mcp'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({
        mode: 'assign-pending-requests',
        pendingRequests: enriched,
        availability,
        ...(revealMap ? { revealMap } : {}),
      }),
      signal: controller.signal,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'unknown' }));
      throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
    }
    const body = (await resp.json()) as { choices?: AiAssignChoice[] };
    return body.choices ?? [];
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Fire-and-forget: starts the background pending-requests sync. Does not poll or surface errors. */
export function fireAndForgetPendingRequestsSync(): void {
  const token = getAdobeAccessToken();
  void fetch(actionUrl('sync-pending-requests-trigger'), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify({}),
  }).catch(err => {
    console.warn('[pendingRequests] background sync trigger failed:', err);
  });
}

/**
 * Fire-and-forget: asks the server to run the background AI Assign pass.
 * The server throttles (minimum interval between runs) and the editor's token
 * is forwarded to the workers. Never polls or surfaces errors.
 */
export function fireAndForgetAiAssignBackground(): void {
  const token = getAdobeAccessToken();
  void fetch(actionUrl('ai-assign-background-trigger'), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify({}),
  }).catch(err => {
    console.warn('[pendingRequests] AI assign background trigger failed:', err);
  });
}

export async function triggerPendingRequestsSync(): Promise<PendingRequestSyncResult> {
  const token = getAdobeAccessToken();

  // 1. Fire the async worker — returns an activationId immediately
  const startResp = await fetch(actionUrl('sync-pending-requests-trigger'), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify({}),
  });
  if (!startResp.ok) {
    const err = await startResp.json().catch(() => ({ error: 'unknown' }));
    throw new Error((err as { error?: string }).error || `HTTP ${startResp.status}`);
  }
  const startData = (await startResp.json()) as { activationId?: string };
  const activationId = startData.activationId;
  if (!activationId) throw new Error('sync-pending-requests-trigger did not return an activationId');

  // 2. Poll sync-status-check until complete, error, or timeout
  const deadline = Date.now() + SYNC_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise<void>(r => setTimeout(r, SYNC_POLL_INTERVAL_MS));
    const pollResp = await fetch(actionUrl('sync-status-check'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ activationId }),
    });
    if (!pollResp.ok) continue;
    const poll = (await pollResp.json()) as { status?: string; lastSyncedAt?: string; error?: string };
    if (poll.status === 'complete') {
      return { synced: true, lastFetchedAt: poll.lastSyncedAt ?? new Date().toISOString() };
    }
    if (poll.status === 'error') {
      throw new Error(poll.error || 'Pending requests sync failed');
    }
    // status === 'running' — keep polling
  }
  throw new Error('Pending requests sync timed out — the job is still running in the background');
}

export async function listReassignmentEvents(requestId: string): Promise<PendingRequestEvent[]> {
  const token = getAdobeAccessToken();
  const resp = await fetch(actionUrl('db-list-reassignment-events'), {
    method: 'POST',
    headers: authHeaders(token || ''),
    body: JSON.stringify({ requestId }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'unknown' }));
    throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
  }
  const body = (await resp.json()) as { events: PendingRequestEvent[] };
  return body.events ?? [];
}
